import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import { Visibility, VisibilityOff, Login as LoginIcon } from '@mui/icons-material';
import { useLogin } from '../hooks/useLogin';
import { ForgotPasswordDialog } from './ForgotPasswordDialog';

export const LoginForm: React.FC = () => {
  const {
    email,
    password,
    showPassword,
    isLoading,
    fieldErrors,
    requires2FA,
    twoFACode,
    setEmail,
    setPassword,
    setTwoFACode,
    toggleShowPassword,
    handleSubmit,
    handleVerify2FA,
  } = useLogin();
  
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  if (requires2FA) {
    return (
      <Box component="form" onSubmit={handleVerify2FA} noValidate>
        <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
          <Box component="p" sx={{ m: 0, fontSize: '0.9rem', color: 'info.dark' }}>
            Um código de verificação foi enviado para <strong>{email}</strong>
          </Box>
        </Box>

        <TextField
          fullWidth
          label="Código de Verificação *"
          type="text"
          value={twoFACode}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
            setTwoFACode(value);
          }}
          margin="normal"
          error={!!fieldErrors.password}
          helperText={fieldErrors.password || 'Digite o código de 6 dígitos recebido por email'}
          autoComplete="one-time-code"
          autoFocus
          inputProps={{
            maxLength: 6,
            pattern: '[0-9]{6}',
            inputMode: 'numeric',
          }}
          sx={{ mb: 3 }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isLoading || twoFACode.length !== 6}
          startIcon={<LoginIcon />}
          sx={{
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 'bold',
            borderRadius: 2,
          }}
        >
          Verificar Código
        </Button>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <TextField
        fullWidth
        label="Email *"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        margin="normal"
        error={!!fieldErrors.email}
        helperText={fieldErrors.email || 'Digite seu email'}
        autoComplete="email"
        autoFocus
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Senha *"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        margin="normal"
        error={!!fieldErrors.password}
        helperText={fieldErrors.password || 'Digite sua senha'}
        autoComplete="current-password"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={toggleShowPassword} edge="end" data-testid="toggle-password">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => setForgotPasswordOpen(true)}
          sx={{ cursor: 'pointer' }}
        >
          Esqueci minha senha
        </Link>
      </Box>

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={isLoading}
        startIcon={<LoginIcon />}
        sx={{
          py: 1.5,
          fontSize: '1.1rem',
          fontWeight: 'bold',
          borderRadius: 2,
        }}
      >
        Entrar
      </Button>

      <ForgotPasswordDialog
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
      />
    </Box>
  );
};


